// Reference photos for a kit — the finished-kit shot and the per-build-step
// shots (ADR-018 phase 4). Both roles are absent from FAMILY_COMPONENT_ROLES,
// REQUIRED_UPLOAD_ROLE_FIELD_KEY, BARCODE_ADVISORY_ROLES and SCAN_ROLE, so no
// photo is ever a file the price or submit gate demands (R13), none raises a
// barcode notice, and none runs print-attribute detection.
//
// ONE fetch covers both roles. Two hooks over the same `listAssets(quote_id)`
// would be two requests for one answer, and their removals would race: the kit
// slot's optimistic drop would be undone by the step strip's next state write.
//
// It shares the transport rather than reimplementing it: the same
// api/uploads.ts calls every other upload goes through, so the accept policy,
// the content-addressed write and the Asset row are identical.

import i18n from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  ASSEMBLY_KIT_PHOTO_ROLE,
  ASSEMBLY_STEP_PHOTO_ROLE,
  isRenderableImage,
  MAX_UPLOAD_BYTES,
  MAX_UPLOAD_MB,
  uploadFileTypeRejection,
  type AssetDto,
  type ComponentRole,
} from 'shared';

import * as uploadsApi from '../api/uploads.js';
import { isAxiosError } from 'axios';
import { extractApiError } from '../api/client.js';

export const STEP_PHOTO_ROLE = ASSEMBLY_STEP_PHOTO_ROLE;
export const KIT_PHOTO_ROLE = ASSEMBLY_KIT_PHOTO_ROLE;

type PhotoRole = typeof ASSEMBLY_STEP_PHOTO_ROLE | typeof ASSEMBLY_KIT_PHOTO_ROLE;

export type PendingPhoto = {
  local_id: string;
  role: PhotoRole;
  // The step this photo illustrates, or null for a finished-kit photo. The kit
  // photo is step-LESS by construction — the upload route refuses a step scope
  // on that role — so this is not "not yet known", it is "does not apply".
  step_id: string | null;
  filename: string;
  preview_url: string | null;
  progress: number;
  error: string | null;
};

// A removal failure, tagged with the slot it happened in. One shared error state
// across two slots would render the kit photo's failure inside the build-step
// section, or nowhere at all — the message has to land next to the control the
// customer just used.
export type PhotoError = {
  role: PhotoRole;
  message: string;
};

export type AssemblyPhotos = {
  // Build-step photos on the quote, keyed by the step each names. A photo whose
  // step_id is null — or whose step the customer has since removed — is the
  // caller's to surface; see BuildStepsEditor's unattached strip.
  stepPhotos: readonly AssetDto[];
  // Finished-kit photos. Never carry a step scope.
  kitPhotos: readonly AssetDto[];
  // In-flight uploads for BOTH roles; a caller filters to its own.
  pending: readonly PendingPhoto[];
  loading: boolean;
  error: PhotoError | null;
  // Whether this mount's photo controls are inert — upload AND remove, both of
  // which already refuse when the hook is disabled. A control that still LOOKS
  // live invites the click and then does nothing, so the strips read this and
  // close themselves. Not the same fact as the caller's own `disabled`: an
  // item's photo controls also close while its line is unsaved, when nothing
  // else about the panel is.
  locked: boolean;
  // Locked specifically because the line has no id yet, rather than because the
  // quote is read-only. The strips render an explanation for this one — a locked
  // slot on a saved order reads back an answer, but here there is no answer yet,
  // only a step the customer has not taken.
  awaitingLine: boolean;
  uploadStep: (files: ArrayLike<File> | null, step_id: string) => void;
  uploadKit: (files: ArrayLike<File> | null) => void;
  remove: (asset: AssetDto) => Promise<void>;
  dismissPending: (local_id: string) => void;
};

const EMPTY: readonly AssetDto[] = [];

export function useAssemblyPhotos(args: {
  quote_id: string | undefined;
  // The `assembly` item whose recipe these photos illustrate. The fetch is per
  // QUOTE and a quote may hold several assembly items, so every mount would
  // otherwise show every other item's photos and file the other recipe's step
  // shots in its own "not attached to a step" strip (ADR-019). A step photo also
  // carries its step_id, but the step-LESS finished-kit photo has only the line.
  //
  // `null` while the item's line is still unsaved: there is no id to claim yet
  // and the upload route refuses a non-uuid. The hook then neither fetches nor
  // writes — a line-less read has no subject, and a line-less upload would land
  // a photo on no kit at all — and reports `awaitingLine` so the strips can tell
  // the customer to save the item first.
  quote_line_id: string | null;
  disabled?: boolean;
}): AssemblyPhotos {
  const { quote_id, quote_line_id: lineScope } = args;
  const awaitingLine = lineScope === null;
  // Inert while the line is unsaved, for upload and remove alike — the same
  // closure the caller's own `disabled` asks for on a read-only order.
  const disabled = args.disabled === true || awaitingLine;
  const [photos, setPhotos] = useState<AssetDto[]>([]);
  const [pending, setPending] = useState<PendingPhoto[]>([]);
  const [error, setError] = useState<PhotoError | null>(null);

  // The subject the photos belong to. The item editor that owns this hook stays
  // mounted while its product type changes, so the scope can move under live
  // state: an upload started for one scope must not land in another's list.
  const scopeKey = quote_id && lineScope !== null ? `${quote_id}:${lineScope}` : null;
  const scopeRef = useRef(scopeKey);
  scopeRef.current = scopeKey;
  // Which scope the list was last loaded for. Loading is derived from it rather
  // than set by the effect, so the render in which the scope changes already
  // reads as loading instead of reading back an empty list for one frame.
  const [loadedScope, setLoadedScope] = useState<string | null>(null);
  const loading = scopeKey !== null && loadedScope !== scopeKey;

  useEffect(() => {
    setPending((cur) => {
      for (const p of cur) if (p.preview_url) URL.revokeObjectURL(p.preview_url);
      return cur.length === 0 ? cur : [];
    });
    setError(null);
    if (!quote_id || lineScope === null) {
      setPhotos([]);
      setLoadedScope(null);
      return;
    }
    const key = `${quote_id}:${lineScope}`;
    let cancelled = false;
    uploadsApi
      .listAssets(quote_id)
      .then((rows) => {
        if (cancelled) return;
        setPhotos(
          rows.filter(
            (r) =>
              (r.component_role === STEP_PHOTO_ROLE || r.component_role === KIT_PHOTO_ROLE) &&
              r.quote_line_id === lineScope,
          ),
        );
      })
      .catch(() => {
        // A failed load is quiet on purpose: the photos are optional direction,
        // and a red box over an optional section reads as a broken order.
        if (!cancelled) setPhotos([]);
      })
      .finally(() => {
        if (!cancelled) setLoadedScope(key);
      });
    return () => {
      cancelled = true;
    };
  }, [quote_id, lineScope]);

  // Revoke every object URL we minted, on unmount only — the per-upload revoke
  // below handles the ordinary path.
  //
  // The list is read through a ref because an unmount cleanup closes over the
  // render that created it. Keyed on `pending`, the cleanup would fire on every
  // change and revoke a preview that is still on screen; with an empty dep array
  // and no ref it closes over the FIRST render's empty list and revokes nothing,
  // leaking every preview a customer left mid-upload.
  const pendingRef = useRef<readonly PendingPhoto[]>(pending);
  useEffect(() => {
    pendingRef.current = pending;
  }, [pending]);
  useEffect(() => {
    return () => {
      for (const p of pendingRef.current) if (p.preview_url) URL.revokeObjectURL(p.preview_url);
    };
  }, []);

  const startUpload = useCallback(
    async (file: File, role: PhotoRole, step_id: string | null): Promise<void> => {
      if (!quote_id || lineScope === null) return;
      const startedFor = `${quote_id}:${lineScope}`;
      // Same shared accept gate the backend enforces, so a rejected file is
      // refused right where it landed instead of after a round-trip. The 'photo'
      // tier is what makes a phone's HEIC acceptable here and nowhere else.
      const rejection =
        uploadFileTypeRejection({
          role: role as ComponentRole,
          filename: file.name,
          content_type: file.type,
        }) ??
        (file.size > MAX_UPLOAD_BYTES
          ? i18n.t('quote:artwork.overUploadLimit', { name: file.name, size: MAX_UPLOAD_MB })
          : null);
      const local_id = `${role}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      if (rejection) {
        setPending((cur) => [
          ...cur,
          {
            local_id,
            role,
            step_id,
            filename: file.name,
            preview_url: null,
            progress: 0,
            error: rejection,
          },
        ]);
        return;
      }
      // A HEIC has no browser-renderable preview, so an object URL for one just
      // produces a broken tile — the filename chip stands in instead. Same
      // predicate the persisted tile uses.
      const preview_url = isRenderableImage(file.type) ? URL.createObjectURL(file) : null;
      setPending((cur) => [
        ...cur,
        { local_id, role, step_id, filename: file.name, preview_url, progress: 0, error: null },
      ]);
      try {
        const asset = await uploadsApi.uploadFile({
          file,
          quote_id,
          component_role: role,
          // Which recipe this photo belongs to — see the prop's own note.
          quote_line_id: lineScope,
          // Omitted entirely for a kit photo: the upload route refuses a step
          // scope on any role but the step one, so sending null would not be
          // ignored, it would be rejected.
          ...(step_id !== null ? { assembly_step_id: step_id } : {}),
          onProgress: (fraction) => {
            setPending((cur) =>
              cur.map((p) => (p.local_id === local_id ? { ...p, progress: fraction } : p)),
            );
          },
        });
        setPending((cur) => {
          const dropped = cur.find((p) => p.local_id === local_id);
          if (dropped?.preview_url) URL.revokeObjectURL(dropped.preview_url);
          return cur.filter((p) => p.local_id !== local_id);
        });
        if (scopeRef.current === startedFor) setPhotos((cur) => [asset, ...cur]);
      } catch (err) {
        const envelope = extractApiError(err);
        const message =
          envelope?.message ??
          (err instanceof Error && !isAxiosError(err) ? err.message : 'That photo didn’t upload. Please try again.');
        setPending((cur) =>
          cur.map((p) => (p.local_id === local_id ? { ...p, error: message, progress: 0 } : p)),
        );
      }
    },
    [quote_id, lineScope],
  );

  const uploadEach = useCallback(
    (files: ArrayLike<File> | null, role: PhotoRole, step_id: string | null): void => {
      if (!files || disabled) return;
      setError(null);
      for (const f of Array.from(files)) void startUpload(f, role, step_id);
    },
    [disabled, startUpload],
  );

  const uploadStep = useCallback(
    (files: ArrayLike<File> | null, step_id: string): void => {
      uploadEach(files, STEP_PHOTO_ROLE, step_id);
    },
    [uploadEach],
  );

  const uploadKit = useCallback(
    (files: ArrayLike<File> | null): void => {
      uploadEach(files, KIT_PHOTO_ROLE, null);
    },
    [uploadEach],
  );

  const remove = useCallback(
    async (asset: AssetDto): Promise<void> => {
      if (disabled) return;
      setError(null);
      setPhotos((cur) => cur.filter((a) => a.id !== asset.id));
      try {
        await uploadsApi.deleteAsset(asset.id);
      } catch {
        setPhotos((cur) => [asset, ...cur]);
        setError({
          role: asset.component_role === KIT_PHOTO_ROLE ? KIT_PHOTO_ROLE : STEP_PHOTO_ROLE,
          message: 'We couldn’t remove that photo. Please try again.',
        });
      }
    },
    [disabled],
  );

  const dismissPending = useCallback((local_id: string): void => {
    setPending((cur) => {
      const dropped = cur.find((p) => p.local_id === local_id);
      if (dropped?.preview_url) URL.revokeObjectURL(dropped.preview_url);
      return cur.filter((p) => p.local_id !== local_id);
    });
  }, []);

  const stepPhotos = useMemo(
    () => (quote_id ? photos.filter((p) => p.component_role === STEP_PHOTO_ROLE) : EMPTY),
    [photos, quote_id],
  );
  const kitPhotos = useMemo(
    () => (quote_id ? photos.filter((p) => p.component_role === KIT_PHOTO_ROLE) : EMPTY),
    [photos, quote_id],
  );

  return {
    stepPhotos,
    kitPhotos,
    pending,
    loading,
    error,
    locked: disabled,
    awaitingLine,
    uploadStep,
    uploadKit,
    remove,
    dismissPending,
  };
}
